import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Mail, Shield, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import requests from "../../services/httpServices";
import {
  getInvitation,
  acceptInvitation,
} from "../../services/inviteServices";
import { useSiteContext } from "../../context/SiteContext";

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setToken, setIsAuthenticated, setUser } = useSiteContext();

  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loggedInEmail, setLoggedInEmail] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const autoAcceptTriedRef = useRef(false);

  // Signup form (only used when the invited user is new)
  const [form, setForm] = useState({
    name: "",
    password: "",
    confirmPassword: "",
  });

  useEffect(() => {
    const loggedIn = !!localStorage.getItem("token");
    setIsLoggedIn(loggedIn);
    try {
      const cached = JSON.parse(localStorage.getItem("user") || "null");
      setLoggedInEmail(cached?.email || null);
    } catch (_) {
      setLoggedInEmail(null);
    }

    const loadInvitation = async () => {
      try {
        const res = await getInvitation(token);
        if (res?.success) {
          setInvitation(res.invitation);
          setForm((prev) => ({ ...prev, name: res.invitation.name || "" }));
        } else {
          // Friendly error messages based on status
          let msg = res?.message || "This invitation is no longer valid.";
          if (res?.status === "accepted") {
            msg = "This invitation has already been accepted. Please log in to access your room.";
          } else if (res?.status === "expired") {
            msg = "This invitation has expired. Please ask the inviter to send a new one.";
          } else if (res?.status === "cancelled") {
            msg = "This invitation has been cancelled.";
          }
          setError(msg);
        }
      } catch (err) {
        const status = err?.response?.status;
        if (status === 404) {
          setError(
            "This invitation link is no longer valid. It may have been removed or replaced. Please ask the inviter to send a new invitation."
          );
        } else {
          setError(
            err?.response?.data?.message || "Failed to load invitation"
          );
        }
      } finally {
        setLoading(false);
      }
    };
    loadInvitation();
  }, [token]);

  // Auto-accept the invitation ONLY when the user just came back from the login
  // redirect (?autoAccept=1 set by the login flow). Direct clicks from the email
  // link should still require an explicit "Accept Invitation" button click.
  useEffect(() => {
    if (autoAcceptTriedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("autoAccept") !== "1") return;
    if (!invitation || !invitation.isExistingUser) return;
    if (!isLoggedIn || !loggedInEmail) return;
    const emailMatches =
      String(loggedInEmail).toLowerCase() ===
      String(invitation.email).toLowerCase();
    if (!emailMatches) return;
    autoAcceptTriedRef.current = true;
    handleAcceptAsExistingUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitation, isLoggedIn, loggedInEmail]);

  // Flow 1: Existing R-Link user (logged in) accepts invitation
  const handleAcceptAsExistingUser = async () => {
    setSubmitting(true);
    try {
      const res = await acceptInvitation(token);
      if (res?.success) {
        toast.success("Invitation accepted");
        navigate("/dashboard");
      } else {
        toast.error(res?.message || "Failed to accept invitation");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to accept invitation"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Flow 2: New user signs up and auto-accepts the invitation
  const handleSignup = async (e) => {
    e.preventDefault();
    if (!form.name || !form.password) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (form.password !== form.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setSubmitting(true);
    try {
      const res = await requests.post("/auth/register", {
        name: form.name,
        email: invitation.email,
        password: form.password,
        inviteToken: token,
      });
      if (res?.success && res?.token) {
        localStorage.setItem("token", res.token);
        localStorage.setItem("user", JSON.stringify(res.user));
        setToken(res.token);
        setIsAuthenticated(true);
        setUser(res.user);
        toast.success("Account created! Welcome to R-Link Studio");
        navigate("/dashboard");
      } else {
        toast.error(res?.message || "Failed to create account");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to create account"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // --- Render states ---
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#001233] text-white">
        <div className="flex items-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin text-[#6a1fbf]" />
          <span>Loading invitation...</span>
        </div>
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#001233] text-white p-6">
        <div className="bg-white/5 rounded-2xl border border-white/10 p-8 max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold mb-2">Invitation Unavailable</h1>
          <p className="text-gray-400 text-sm mb-6">
            {error || "This invitation is invalid or has expired."}
          </p>
          <button
            onClick={() => navigate("/login")}
            className="px-6 py-2.5 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#001233] text-white p-6">
      <div className="bg-white/5 rounded-2xl border border-white/10 p-8 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#6a1fbf] to-[#00c853] flex items-center justify-center mx-auto mb-4">
            <Mail className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold mb-1">You're Invited!</h1>
          <p className="text-gray-400 text-sm">
            {invitation.inviter?.name || "Someone"} has invited you to join their team on R-Link Studio. Create your account below to get started.
          </p>
        </div>

        {/* Invitation details */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6 space-y-2">
          <div className="flex items-start justify-between gap-3 text-sm">
            <span className="text-gray-400 flex-shrink-0">Email</span>
            <span className="text-white font-medium text-right break-all min-w-0">
              {invitation.email}
            </span>
          </div>
          <div className="flex items-start justify-between gap-3 text-sm">
            <span className="text-gray-400 flex-shrink-0">Role</span>
            <span className="text-white font-medium flex items-center gap-1">
              <Shield className="w-3.5 h-3.5 text-[#00c853]" />
              {invitation.role}
            </span>
          </div>
          {invitation.roomName && (
            <div className="flex items-start justify-between gap-3 text-sm">
              <span className="text-gray-400 flex-shrink-0">Room</span>
              <span className="text-white font-medium text-right break-words min-w-0">
                {invitation.roomName}
              </span>
            </div>
          )}
          {invitation.inviter?.companyName && (
            <div className="flex items-start justify-between gap-3 text-sm">
              <span className="text-gray-400 flex-shrink-0">Organization</span>
              <span className="text-white font-medium text-right break-words min-w-0">
                {invitation.inviter.companyName}
              </span>
            </div>
          )}
        </div>

        {/* Flow branching */}
        {invitation.isExistingUser ? (
          (() => {
            const emailMatches =
              loggedInEmail &&
              invitation?.email &&
              String(loggedInEmail).toLowerCase() === String(invitation.email).toLowerCase();

            if (isLoggedIn && emailMatches) {
              // Logged in as the invited user → one-click accept
              return (
                <button
                  onClick={handleAcceptAsExistingUser}
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4" />
                  )}
                  {submitting ? "Accepting..." : "Accept Invitation"}
                </button>
              );
            }

            if (isLoggedIn && !emailMatches) {
              // Logged in as a different user → must log out first
              return (
                <div className="space-y-3">
                  <p className="text-sm text-gray-400 text-center">
                    You're signed in as <strong className="text-white">{loggedInEmail}</strong>,
                    but this invitation was sent to{" "}
                    <strong className="text-white">{invitation.email}</strong>.
                    Please log in with the invited account.
                  </p>
                  <button
                    onClick={() => {
                      localStorage.removeItem("token");
                      localStorage.removeItem("user");
                      navigate(
                        `/login?redirect=${encodeURIComponent(`/invite/${token}`)}`
                      );
                    }}
                    className="w-full px-6 py-3 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
                  >
                    Sign out & log in to Accept
                  </button>
                </div>
              );
            }

            // Not logged in
            return (
              <div className="space-y-3">
                <p className="text-sm text-gray-400 text-center">
                  You already have an R-Link account. Please log in to accept.
                </p>
                <button
                  onClick={() =>
                    navigate(
                      `/login?redirect=${encodeURIComponent(`/invite/${token}`)}`
                    )
                  }
                  className="w-full px-6 py-3 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  Log In to Accept
                </button>
              </div>
            );
          })()
        ) : (
          // New user: signup form
          <form onSubmit={handleSignup} className="space-y-4" autoComplete="off">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Full Name
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                autoComplete="off"
                name="fullName"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] text-sm"
                placeholder="John Doe"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <input
                type="password"
                value={form.password}
                onChange={(e) =>
                  setForm({ ...form, password: e.target.value })
                }
                autoComplete="new-password"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] text-sm"
                placeholder="At least 6 characters"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Confirm Password
              </label>
              <input
                type="password"
                value={form.confirmPassword}
                onChange={(e) =>
                  setForm({ ...form, confirmPassword: e.target.value })
                }
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#6a1fbf] focus:ring-1 focus:ring-[#6a1fbf] text-sm"
                placeholder="Re-enter password"
                required
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting ? "Creating Account..." : "Create Account & Join"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AcceptInvite;
